/**
 * SerialBus.js — Bus compartido para Web Serial
 *
 * El DeviceManager (F6) es el único dueño del puerto físico.
 * Este singleton permite que otros módulos (WslRuntime, Transport)
 * envíen comandos JSON y reciban respuestas sin abrir un segundo puerto.
 *
 * Protocolo request-response (igual que LocalTransport):
 *   → {"cmd":"READ_ALL"}\n
 *   ← {"tag1":val,...}\n
 *
 *   → {"cmd":"SET","tag":"x","value":1}\n
 *   ← {"ok":true}\n
 *
 * Uso:
 *   // En DeviceManager, al conectar el puerto:
 *   SerialBus.attach(port, writer);
 *
 *   // En DeviceManager, al recibir cada línea:
 *   SerialBus.receiveLine(rawLine);
 *
 *   // En DeviceManager, al desconectar:
 *   SerialBus.detach();
 *
 *   // En Transport/WslRuntime:
 *   const data = await SerialBus.sendCommand({ cmd: 'READ_ALL' });
 */

class _SerialBus extends EventTarget {
  constructor() {
    super();
    this._writer    = null;  // WritableStreamDefaultWriter
    this._encoder   = new TextEncoder();
    this._connected = false;
    this._pending   = null;  // { resolve, reject, timeout }

    // Observers de líneas crudas (para el log de DeviceManager)
    this._lineListeners = new Set();
  }

  // ── Llamado por DeviceManager ────────────────────────────────────────────

  /**
   * Registra el puerto recién abierto.
   * @param {WritableStreamDefaultWriter} writer
   */
  attach(writer) {
    this._writer    = writer;
    this._connected = true;
    this.dispatchEvent(new Event('connected'));
  }

  /** Llamado por DeviceManager cuando cierra el puerto. */
  detach() {
    this._connected = false;
    this._writer    = null;
    // Rechazar cualquier petición pendiente
    if (this._pending) {
      clearTimeout(this._pending.timeout);
      this._pending.reject(new Error('Puerto serial desconectado'));
      this._pending = null;
    }
    this.dispatchEvent(new Event('disconnected'));
  }

  /**
   * DeviceManager llama esto por cada línea recibida del ESP32.
   * El bus despacha la línea a: (a) petición pendiente, (b) listeners de telemetría.
   * @param {string} line — línea cruda sin \n
   */
  receiveLine(line) {
    // Notificar a listeners de líneas crudas (para log en DeviceManager)
    this._lineListeners.forEach(fn => fn(line));

    // Si hay una petición request-response pendiente, entregarle la respuesta
    if (this._pending) {
      const { resolve, reject, timeout } = this._pending;
      this._pending = null;
      clearTimeout(timeout);
      try {
        resolve(JSON.parse(line));
      } catch {
        reject(new Error(`Respuesta serial inválida: ${line}`));
      }
    }
    // Si NO hay petición pendiente, podría ser telemetría proactiva (push)
    // → la despacha como evento para que Transport la consuma directamente
    else {
      try {
        const obj = JSON.parse(line);
        this.dispatchEvent(Object.assign(new Event('telemetry'), { data: obj }));
      } catch { /* línea de log no-JSON, ignorar */ }
    }
  }

  // ── Llamado por Transport / WslRuntime ───────────────────────────────────

  isConnected() { return this._connected; }

  /**
   * Suscribirse a líneas crudas (para que DeviceManager pueda seguir
   * mostrándolas en su log aunque el bus las haya interceptado).
   */
  onLine(fn) {
    this._lineListeners.add(fn);
    return () => this._lineListeners.delete(fn);
  }

  /**
   * Envía un comando JSON y espera la respuesta.
   * Solo un comando a la vez (protocolo request-response).
   * @param {object} obj
   * @param {number} [timeoutMs=3000]
   * @returns {Promise<object>}
   */
  /**
   * Envía una línea de texto cruda sin esperar respuesta.
   * @param {string} text — debe terminar en \n
   */
  sendRaw(text) {
    if (!this._connected || !this._writer) {
      return Promise.reject(new Error('Puerto serial no conectado (SerialBus)'));
    }
    return this._writer.write(this._encoder.encode(text));
  }

  sendCommand(obj, timeoutMs = 3000) {
    if (!this._connected || !this._writer) {
      return Promise.reject(new Error('Puerto serial no conectado (SerialBus)'));
    }
    if (this._pending) {
      return Promise.reject(new Error('SerialBus: petición anterior aún pendiente'));
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this._pending = null;
        reject(new Error('Timeout esperando respuesta serial'));
      }, timeoutMs);

      this._pending = { resolve, reject, timeout };

      const line = JSON.stringify(obj) + '\n';
      this._writer.write(this._encoder.encode(line)).catch(err => {
        clearTimeout(timeout);
        this._pending = null;
        reject(err);
      });
    });
  }
}

/** Singleton global — se importa igual en cualquier módulo. */
export const SerialBus = new _SerialBus();
